import React, { useState } from "react";

import {
  GetAll,
  Delete,
} from "adminApi/RoomNoFloorAssign/RoomNoFloorAssign";

import DataTable from "Common/DataTable/DataTable";

import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";

import { generateDisplayFields } from "utils/fieldMapper";

import fields from "../../json/RoomNoFloorAssignFields.json";

import { useRoleVerify } from "Common/RoleVerify";

/* =========================================================
   STATUS FORMAT
========================================================= */

const formatStatus = (value) => {
  switch (Number(value)) {
    case 0:
      return "Pending";

    case 1:
      return "Available";

    case 2:
      return "Booked";

    case 3:
      return "Cleaning";

    case 4:
      return "Check-In";

    case 5:
      return "Check-Out";

    case 6:
      return "Dirty";

    case 7:
      return "Blocked";

    case 8:
      return "Do Not Reserve";

    case 9:
      return "Occupied";

    default:
      return "-";
  }
};

const RoomNoFloorAssignListTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(
    search,
    500,
  );

  const canShowAction = useRoleVerify("", [
    "can_edit",
    "can_delete",
  ]);

  const sizeOptions = [10, 20, 50, 100];

  const { tabledata } = fields;

  /* =======================================================
     FETCH
  ======================================================= */

  const {
    data,
    meta,
    loading,
    error,
    refetch,
  } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "Room Floor Assignment List",
  });

  /* =======================================================
     DISPLAY FIELDS
  ======================================================= */

  const displayFields =
    generateDisplayFields(tabledata);

  /* =======================================================
     FORMAT DATA
  ======================================================= */

  const formattedSource = (data || []).map(
    (item) => ({
      ...item,
      status: formatStatus(item.status),
    }),
  );

  /* =======================================================
     TABLE
  ======================================================= */

  const {
    formattedData,
    columns,
  } = useTableConfig({
    data: formattedSource,
    page,
    limit,
    canShowAction,
    refetch,

    config: {
      entityName: "Room Floor Assignment",
      editUrl: "room-no-floor-assign-edit",
      idKey: "room_assign_id",
      deleteApi: Delete,
      displayFields,
    },
  });

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch = (
    value,
    resetPage = null,
  ) => {
    setSearch(value);

    if (resetPage !== null) {
      setPage(resetPage);
    }
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={formattedData}
      options={{
        loading,
        error,

        sortableColumns:
          displayFields.map(
            (field) => field.accessor,
          ),

        copy: true,
        csv: true,
        pdf: true,
        print: true,

        fileName:
          "Room Floor Assignment List",
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default RoomNoFloorAssignListTable;